import React, { useState, useEffect } from 'react';
import SearchableSelect from '../common/SearchableSelect';

const SocietyFilters = ({ 
  cities, 
  filters, 
  onFilterChange, 
  onSearch, 
  onCityChange, 
  onSortChange 
}) => {
  const [searchTerm, setSearchTerm] = useState(filters.search);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    setSearchTerm(filters.search || '');
  }, [filters.search]);

  const activeFilterCount =
    (filters.search ? 1 : 0) + (filters.city ? 1 : 0) + (filters.minRating > 0 ? 1 : 0);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onSearch(searchTerm);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCityChange = (e) => {
    const city = e.target.value;
    onCityChange(city);
  };

  const handleSortChange = (e) => {
    const sortBy = e.target.value;
    onSortChange(sortBy);
  };

  const handleRatingFilter = (rating) => {
    onFilterChange({ minRating: rating });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setMobileFiltersOpen(false);
    onFilterChange({
      search: '',
      city: '',
      sortBy: 'rating',
      minRating: 0
    });
  };

  const fieldGap = 'mb-4 lg:mb-6';
  const labelCls = 'block text-xs font-medium text-gray-700 mb-1.5 lg:text-sm lg:mb-2';

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm lg:border-0 lg:shadow-sm">
      {/* Mobile: compact summary — full filter form hidden until expanded so listings stay visible */}
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 lg:hidden">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">Filter & Sort</h3>
          {activeFilterCount > 0 ? (
            <p className="text-xs text-gray-500">{activeFilterCount} active</p>
          ) : (
            <p className="text-xs text-gray-500">Tap to refine results</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setMobileFiltersOpen((o) => !o)}
          aria-expanded={mobileFiltersOpen}
          aria-controls="society-filters-panel"
          className="shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 min-h-[44px]"
        >
          {mobileFiltersOpen ? 'Hide' : 'Show filters'}
        </button>
      </div>

      <div
        id="society-filters-panel"
        className={`${mobileFiltersOpen ? 'block' : 'hidden'} max-h-[min(70vh,560px)] overflow-y-auto border-t border-gray-100 px-3 pb-3 pt-3 lg:!block lg:max-h-none lg:overflow-visible lg:border-t-0 lg:p-6 lg:pt-6`}
      >
      <h3 className="mb-4 hidden text-lg font-semibold text-gray-900 lg:mb-6 lg:block">Filter & Sort</h3>

      {/* Search */}
      <div className={fieldGap}>
        <label className={labelCls} htmlFor="society-filter-search">
          Search Societies
        </label>
        <form onSubmit={handleSearchSubmit} className="flex">
          <input
            id="society-filter-search"
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search by name, description, or city..."
            className="min-h-[44px] min-w-0 flex-1 rounded-l-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            aria-label="Search"
            className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-r-md bg-blue-600 px-3 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        </form>
      </div>

      {/* City Filter */}
      <div className={fieldGap}>
        <SearchableSelect
          id="society-filter-city"
          name="city"
          label="City"
          value={filters.city}
          onChange={handleCityChange}
          options={cities && Array.isArray(cities) ? cities : []}
          searchable
          emptyOption={{ value: '', label: 'All Cities' }}
          searchPlaceholder="Search cities…"
        />
      </div>

      {/* Sort By */}
      <div className={fieldGap}>
        <label className={labelCls} htmlFor="society-filter-sort">
          Sort By
        </label>
        <select
          id="society-filter-sort"
          value={filters.sortBy}
          onChange={handleSortChange}
          className="min-h-[44px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="rating">Rating (High to Low)</option>
          <option value="-rating">Rating (Low to High)</option>
          <option value="name">Name (A to Z)</option>
          <option value="-name">Name (Z to A)</option>
          <option value="created_at">Newest First</option>
          <option value="-created_at">Oldest First</option>
          <option value="total_ratings">Most Rated</option>
          <option value="-total_ratings">Least Rated</option>
        </select>
      </div>

      {/* Rating Filter */}
      <div className={fieldGap}>
        <span className={labelCls}>Minimum Rating</span>
        <div className="flex flex-wrap gap-x-3 gap-y-2 lg:flex-col lg:gap-y-2">
          {[0, 1, 2, 3, 4].map((rating) => (
            <label key={rating} className="flex min-h-[44px] min-w-0 flex-1 cursor-pointer items-center rounded-md border border-gray-200 px-2 py-1.5 sm:flex-none sm:px-3 lg:w-full lg:border-0 lg:px-0 lg:py-0">
              <input
                type="radio"
                name="minRating"
                value={rating}
                checked={filters.minRating === rating}
                onChange={() => handleRatingFilter(rating)}
                className="h-4 w-4 shrink-0 border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-xs text-gray-700 sm:text-sm">
                {rating === 0 ? 'All' : `${rating}+`}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Quick Filters */}
      <div className={fieldGap}>
        <span className={labelCls}>Quick Filters</span>
        <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-col lg:gap-2">
          <button
            type="button"
            onClick={() => onFilterChange({ minRating: 4 })}
            className="min-h-[44px] rounded-md border border-gray-200 px-2 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50 sm:text-sm lg:w-full lg:border-0 lg:px-3"
          >
            ⭐ 4+ stars
          </button>
          <button
            type="button"
            onClick={() => onFilterChange({ minRating: 3 })}
            className="min-h-[44px] rounded-md border border-gray-200 px-2 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50 sm:text-sm lg:w-full lg:border-0 lg:px-3"
          >
            ⭐ 3+ stars
          </button>
          <button
            type="button"
            onClick={() => onFilterChange({ sortBy: 'created_at' })}
            className="min-h-[44px] rounded-md border border-gray-200 px-2 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50 sm:text-sm lg:w-full lg:border-0 lg:px-3"
          >
            🆕 Newest
          </button>
          <button
            type="button"
            onClick={() => onFilterChange({ sortBy: 'total_ratings' })}
            className="min-h-[44px] rounded-md border border-gray-200 px-2 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50 sm:text-sm lg:w-full lg:border-0 lg:px-3"
          >
            🔥 Popular
          </button>
        </div>
      </div>

      {/* Clear Filters */}
      <button
        type="button"
        onClick={clearFilters}
        className="min-h-[44px] w-full rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
      >
        Clear All Filters
      </button>

      {/* Active Filters Display */}
      {(filters.search || filters.city || filters.minRating > 0) && (
        <div className="mt-4 border-t border-gray-200 pt-4 lg:mt-6 lg:pt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Active Filters:</h4>
          <div className="flex flex-wrap gap-2">
            {filters.search && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Search: "{filters.search}"
                <button
                  onClick={() => onSearch('')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}
            {filters.city && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                City: {filters.city}
                <button
                  onClick={() => onCityChange('')}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            )}
            {filters.minRating > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Rating: {filters.minRating}+ stars
                <button
                  onClick={() => handleRatingFilter(0)}
                  className="ml-1 text-yellow-600 hover:text-yellow-800"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default SocietyFilters;
